import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import {
  getDefaultVisibleColumnIds,
  getLockedColumnIds,
  normalizeVisibleColumns,
  widthsForVisibleColumns,
  minWidthsForVisibleColumns,
} from '../config/listViewColumns';
import { useResizableColumns } from './useResizableColumns';

const PREFERENCES_QUERY_KEY = 'user-preferences';

export function useListViewPreferences(viewKey, allColumns) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const lockedIds = useMemo(() => getLockedColumnIds(allColumns), [allColumns]);
  const defaultIds = useMemo(() => getDefaultVisibleColumnIds(allColumns), [allColumns]);

  const { data: preferences = {}, isLoading } = useQuery(
    PREFERENCES_QUERY_KEY,
    () => api.get('/users/me/preferences').then((r) => r.data.preferences || {}),
    {
      staleTime: 30_000,
      enabled: Boolean(user?.id),
      keepPreviousData: true,
    }
  );

  const saveMutation = useMutation(
    (patch) => api.patch('/users/me/preferences', patch).then((r) => r.data.preferences),
    {
      onSuccess: (next) => {
        queryClient.setQueryData(PREFERENCES_QUERY_KEY, next);
      },
    }
  );

  const savedView = useMemo(
    () => preferences?.listViews?.[viewKey] || {},
    [preferences, viewKey]
  );

  const visibleColumnIds = useMemo(
    () => normalizeVisibleColumns(savedView.columns, allColumns, lockedIds, defaultIds),
    [savedView.columns, allColumns, lockedIds, defaultIds]
  );

  const persistView = useCallback(
    (partial) => {
      saveMutation.mutate({
        listViews: {
          [viewKey]: {
            ...savedView,
            ...partial,
          },
        },
      });
    },
    [saveMutation, savedView, viewKey]
  );

  const setVisibleColumnIds = useCallback(
    (columnIds) => {
      const normalized = normalizeVisibleColumns(columnIds, allColumns, lockedIds, defaultIds);
      persistView({ columns: normalized });
    },
    [allColumns, lockedIds, defaultIds, persistView]
  );

  const toggleColumn = useCallback(
    (columnId) => {
      if (lockedIds.includes(columnId)) return;
      const set = new Set(visibleColumnIds);
      if (set.has(columnId)) {
        const dataCols = [...set].filter((id) => !lockedIds.includes(id));
        if (dataCols.length <= 1 && dataCols[0] === columnId) return;
        set.delete(columnId);
      } else {
        set.add(columnId);
      }
      const lockedOrdered = allColumns
        .map((c) => c.id)
        .filter((id) => lockedIds.includes(id) && set.has(id));
      const unlockedExisting = visibleColumnIds.filter(
        (id) => set.has(id) && !lockedIds.includes(id)
      );
      const unlockedAdded = allColumns
        .map((c) => c.id)
        .filter((id) => set.has(id) && !lockedIds.includes(id) && !unlockedExisting.includes(id));
      setVisibleColumnIds([...lockedOrdered, ...unlockedExisting, ...unlockedAdded]);
    },
    [allColumns, lockedIds, setVisibleColumnIds, visibleColumnIds]
  );

  const moveColumn = useCallback(
    (columnId, direction) => {
      if (lockedIds.includes(columnId)) return;
      const unlocked = visibleColumnIds.filter((id) => !lockedIds.includes(id));
      const idx = unlocked.indexOf(columnId);
      if (idx < 0) return;
      const next = direction === 'up' ? idx - 1 : idx + 1;
      if (next < 0 || next >= unlocked.length) return;
      const swapped = [...unlocked];
      [swapped[idx], swapped[next]] = [swapped[next], swapped[idx]];
      const lockedOrdered = allColumns
        .map((c) => c.id)
        .filter((id) => lockedIds.includes(id) && visibleColumnIds.includes(id));
      setVisibleColumnIds([...lockedOrdered, ...swapped]);
    },
    [allColumns, lockedIds, setVisibleColumnIds, visibleColumnIds]
  );

  const resetColumns = useCallback(() => {
    setVisibleColumnIds(defaultIds);
  }, [defaultIds, setVisibleColumnIds]);

  const isVisible = useCallback(
    (columnId) => visibleColumnIds.includes(columnId),
    [visibleColumnIds]
  );

  const widthKey = `${viewKey}-widths-v1`;
  const initialWidths = useMemo(
    () => widthsForVisibleColumns(visibleColumnIds, allColumns),
    [visibleColumnIds, allColumns]
  );
  const columnMinWidths = useMemo(
    () => minWidthsForVisibleColumns(visibleColumnIds, allColumns),
    [visibleColumnIds, allColumns]
  );

  const resizable = useResizableColumns(initialWidths, widthKey, columnMinWidths);

  const colIndex = useCallback(
    (columnId) => visibleColumnIds.indexOf(columnId),
    [visibleColumnIds]
  );

  return {
    preferences,
    savedView,
    visibleColumnIds,
    isLoading,
    isSaving: saveMutation.isLoading,
    toggleColumn,
    moveColumn,
    resetColumns,
    isVisible,
    colIndex,
    persistView,
    setVisibleColumnIds,
    allColumns,
    lockedIds,
    ...resizable,
  };
}
